import React, { useEffect, useLayoutEffect } from 'react';
import { useSharedValue } from 'react-native-reanimated';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import { AppView, SCREEN_BOTTOM_PADDING, ScrollView } from 'design-system';
import { IconSettings } from 'design-system/icons/IconSettings';
import NavigationHeaderRightIconButton from 'design-system/NavigationHeaderRightIconButton';
import NavigationHeaderTitle from 'design-system/NavigationHeaderTitle';
import { SpaceUserAvatar } from 'design-system/SpaceUserAvatar';
import StatusBar from 'design-system/StatusBar';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { selectIsDefaultSpace } from 'features/spaces/reducers/selectors';
import { selectUserId } from 'reducers/selectors';
import { HEADER_WITH_SUBTITLE_HEIGHT } from 'navigators/styles';
import { showUnavailableOnWeb } from 'utils/webGate';
import { isWeb } from '../../../constants';
import { getAutosaveActivities } from '../actions';
import PotsActivitiesContainer from '../components/PotsActivitiesContainer';
import PotScreenHeader from '../components/PotScreenHeader';
import { selectPotFromPotId } from '../reducers/selectors';
const PotScreen = ({ route, navigation }) => {
    const colors = useColors();
    const scrollY = useSharedValue(0);
    const routePot = route.params?.screenParams;
    const hideSpaceUserAvatar = route.params?.hideSpaceUserAvatar;
    const potFromStore = useAppSelector((state) => selectPotFromPotId(state, routePot.id));
    // Spread so a route-param clone (e.g. after a bank top-up) still produces a
    // new object identity, which downstream [pot] effects rely on to refetch
    const pot = React.useMemo(() => (potFromStore ? { ...potFromStore } : routePot), [potFromStore, routePot]);
    const savingsPlanMandateStatus = useAppSelector((state) => state.pots.savingsPlan?.mandateStatus);
    const dispatch = useAppDispatch();
    const store = useAppStore();
    const { userId: potOwnerUserId } = pot;
    const myId = useAppSelector(selectUserId);
    const isDefaultSpace = useAppSelector(selectIsDefaultSpace);
    const isPotOwner = myId === potOwnerUserId;
    useEffect(() => {
        const state = store.getState();
        if (savingsPlanMandateStatus &&
            !state.unpersistedPotsReducer.fetchingAutosaveActivities &&
            !state.unpersistedPotsReducer.autoSaveActivities?.length) {
            // This is so we can show an alert if the user tries to delete with pending activities
            dispatch(getAutosaveActivities({
                page: 1,
                perPage: 30,
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useLayoutEffect(() => {
        const onPress = () => {
            if (showUnavailableOnWeb('emma://save?tab=pots', 'Pot settings')) {
                return;
            }
            navigation.navigateDeprecated('PotSettings', {
                pot,
            });
        };
        navigation.setOptions({
            headerTitle: () => (<NavigationHeaderTitle title={pot.name}>
          {!hideSpaceUserAvatar ? <SpaceUserAvatar xxSmall userId={pot.userId}/> : null}
        </NavigationHeaderTitle>),
            headerTintColor: colors.elements.primary,
            headerRight: !isDefaultSpace && !isPotOwner
                ? undefined
                : () => (<NavigationHeaderRightIconButton onPress={onPress}>
                <IconSettings color={colors.elements.primary}/>
              </NavigationHeaderRightIconButton>),
        });
    }, [colors.elements.primary, pot, isDefaultSpace, isPotOwner, hideSpaceUserAvatar]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const paddingBottom = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING, paddingHorizontalStyle);
    return (<AppView withFrame={false}>
      <StatusBar barStyle="dark-content" animated/>
      <ScrollView secondary scrollY={scrollY} keyboardShouldPersistTaps="handled" contentContainerStyle={paddingBottom} finalHeaderHeight={HEADER_WITH_SUBTITLE_HEIGHT}>
        <PotScreenHeader pot={pot} isDefaultSpace={isDefaultSpace} isPotOwner={isPotOwner}/>
        <PotsActivitiesContainer hideSpaceUserAvatar={hideSpaceUserAvatar} pot={pot}/>
      </ScrollView>
    </AppView>);
};
export default PotScreen;
